import type {
  FileDetailPayload,
  FileEntry,
  ParticipantCardModel,
  ParticipantsResponse,
  SixActivity,
  SixDetailMeta,
} from '@/types/SixDetail.types';
import { formatDate } from '@/utils/format';
import { formatBytes } from '@/utils/format.utils';
import type {
  ParticipantMemberDTO,
  ParticipantsPayloadDTO,
  SixActivityDTO,
  SixFilesApiResponse,
  SixFileDTO,
  SixFolderDTO,
  SixSummaryDTO,
} from '@/types/SixDetailDTOs';
import type { FileMetadata } from '@/types/poc/helia';

export function mapSixSummaryDTOToSixDetailMeta(dto: SixSummaryDTO): SixDetailMeta {
  return {
    ...dto,
    createdAt: new Date(dto.createdAt),
    lastModified: new Date(dto.lastModified),
  };
}

export function mapSixActivityDTOToSixActivity(dto: SixActivityDTO): SixActivity {
  return {
    id: dto.id,
    action: dto.action,
    description: dto.description.join(', '),
    userName: dto.userName,
    timestamp: new Date(dto.timestamp),
  };
}

/**
 * Maps an array of SixActivityDTO to SixActivity[]
 */
export function mapSixActivitiesDTOToSixActivities(dtos: SixActivityDTO[]): SixActivity[] {
  return dtos.map(mapSixActivityDTOToSixActivity);
}

function formatTimestamp(value: number | string | undefined): string {
  if (typeof value === 'number') {
    return formatDate(new Date(value).toISOString());
  }

  if (typeof value === 'string') {
    return formatDate(value);
  }

  return '-';
}

function getNonEmptyString(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();

  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

function getUploadedByUserName(dto: Pick<SixFileDTO, 'uploadedByUserName' | 'uploadedByUser'>): string | undefined {
  return getNonEmptyString(dto.uploadedByUserName) ?? getNonEmptyString(dto.uploadedByUser);
}

function getPathLeafName(path: string): string {
  const normalizedPath = path.replace(/\/+$/, '');
  const segments = normalizedPath.split('/').filter(Boolean);

  return segments.length > 0 ? segments[segments.length - 1] : path;
}

function getFileTypeLabel(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
    return 'FILE';
  }

  return fileName.substring(lastDotIndex + 1).toUpperCase();
}

export function mapSixFileDTOToFileEntry(dto: SixFileDTO): FileEntry {
  return {
    id: dto.id,
    cid: dto.cid,
    name: dto.name,
    kind: 'file',
    mimeType: dto.type,
    fileTypeLabel: getFileTypeLabel(dto.name),
    rawSizeBytes: dto.size,
    rawLastModified: dto.lastModified,
    size: formatBytes(dto.size),
    modified: formatTimestamp(dto.lastModified),
    path: dto.path,
    isFavorite: dto.isFavorite,
    uploadedByUser: getUploadedByUserName(dto),
    uploadedByUserId: dto.uploadedByUserId,
    downloadedByUserName: dto.downloadedByUserName,
    encryptedFileKeys: dto.encryptedFileKeys,
  };
}

export function mapSixFolderDTOToFileEntry(dto: SixFolderDTO): FileEntry {
  return {
    id: dto.id,
    name: getPathLeafName(dto.path),
    kind: 'folder',
    fileTypeLabel: 'FOLDER',
    rawLastModified: dto.lastModified,
    modified: formatTimestamp(dto.lastModified),
    created: formatTimestamp(dto.createdAt),
    path: dto.path,
    uploadedByUser: dto.createdByUserName,
    createdByUser: dto.createdByUserName,
    createdByUserId: dto.createdByUserId,
    itemCount: dto.itemCount,
  };
}

export function mapSixFilesApiResponseToFileEntries(dto: SixFilesApiResponse): FileEntry[] {
  const folders = dto.folders.map(mapSixFolderDTOToFileEntry);
  const files = dto.files.map(mapSixFileDTOToFileEntry);

  return [...folders, ...files];
}

export function mapFileEntryToFileMetadata(entry: FileEntry, sixId: string): FileMetadata {
  const uploadedByUser = getNonEmptyString(entry.uploadedByUser) ?? '';

  return {
    id: entry.id,
    cid: entry.cid ?? '',
    name: entry.name,
    size: entry.rawSizeBytes ?? 0,
    type: entry.mimeType ?? 'application/octet-stream',
    addedBy: uploadedByUser,
    lastModified: entry.rawLastModified ?? entry.modified,
    sixId,
    path: entry.path ?? '',
    uploadedByUserName: uploadedByUser,
    uploadedByUserId: entry.uploadedByUserId ?? '',
    decryptionCIDS: Object.entries(entry.encryptedFileKeys ?? {}).map(([userId, { cid }]) => ({
      userId,
      cid,
    })),
  };
}
function mapMemberToParticipant(member: ParticipantMemberDTO): ParticipantsResponse['owners'][number] {
  return {
    id: member.id,
    name: member.name,
    email: member.email,
    title: undefined,
    phone: undefined,
    department: undefined,
    organization: undefined,
  };
}

/**
 * Maps the raw participants API payload (ParticipantsPayloadDTO) to the
 * domain ParticipantsResponse used in Redux state.
 *
 * The API returns a flat `members` array with role field; this mapper
 * groups them into owners, internalParticipants, and externalParticipants.
 */
export function mapParticipantsPayloadDTOToParticipantsResponse(
  dto: ParticipantsPayloadDTO
): ParticipantsResponse {
  const members = Array.isArray(dto.members) ? dto.members : [];

  const owners = members.filter((member) => member.role === 'Owner').map(mapMemberToParticipant);
  const internalParticipants = members.filter((member) => member.role === 'InternalParticipant').map(mapMemberToParticipant);
  const externalParticipants = members.filter((member) => member.role === 'ExternalParticipant').map(mapMemberToParticipant);

  return { owners, internalParticipants, externalParticipants };
}

export function mapParticipantsToParticipantCards(
  participants?: ParticipantsResponse
): ParticipantCardModel[] {
  if (participants === undefined) return [];

  const owners = participants.owners.map((participant) => ({
    id: participant.id,
    name: participant.name,
    title: participant.title,
    email: participant.email,
    role: 'owner' as const,
  }));

  const internal = participants.internalParticipants.map((participant) => ({
    id: participant.id,
    name: participant.name,
    title: participant.title,
    email: participant.email,
    role: 'member' as const,
  }));

  const external = participants.externalParticipants.map((participant) => ({
    id: participant.id,
    name: participant.name,
    title: participant.title,
    email: participant.email,
    role: 'externalMember' as const,
  }));

  return [...owners, ...internal, ...external];
}

export function mapFileEntryToFileDetailPayload(
  entry: FileEntry,
  totalMembers: number,
): FileDetailPayload {
  const uploadedByUser = getNonEmptyString(entry.uploadedByUser);

  if (entry.kind === 'folder') {
    return {
      type: 'folder',
      name: entry.name,
      itemCount: entry.itemCount ?? 0,
      created: entry.created ?? '-',
      createdBy: getNonEmptyString(entry.createdByUser) ?? uploadedByUser ?? '-',
    };
  }

  const downloadNames = entry.downloadedByUserName ?? [];

  return {
    type: 'file',
    name: entry.name,
    fileTypeLabel: entry.fileTypeLabel ?? 'FILE',
    size: entry.size ?? formatBytes(entry.rawSizeBytes ?? 0),
    uploadedBy: uploadedByUser ?? '-',
    downloads: {
      count: downloadNames.length,
      totalMembers: totalMembers > 0 ? totalMembers : downloadNames.length,
      names: downloadNames,
    },
  };
}
